import {
  useGetActiveSubscriptionPlans,
  useSendPlanCancellationRequest,
} from "@/hooks/use-subscription";
import {
  IActiveSubscriptionPlan,
  IPlanCancellationRequestPayload,
  IPurchasePlan,
  ReturnLikelihood,
} from "@/types/subscription.types";
import { useSearch } from "@tanstack/react-router";

import { CancelPlanSearch } from "@/routes/cancelPlan";
import { createContext, useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import * as z from "zod";
import {
  FeedbackFormValues,
  formatCancellationReason,
} from "./cancel-feedback";

const valuableFeaturesSchema = z.object({
  emailWarmup: z.boolean(),
  inboxPlacementTests: z.boolean(),
  googlePostmasterIntegration: z.boolean(),
  emailHealthTests: z.boolean(),
  customTemplates: z.boolean(),
  emailTemplateCoach: z.boolean(),
});

const cancellationPayloadSchema = z
  .object({
    feedback: z.object({
      cancellationReason: z.object({
        type: z.string().min(1, "Cancellation reason is required"),
        details: z.record(z.string()),
      }),
      improvementSuggestions: z.string(),
      serviceRating: z.number().min(0).max(5),
      valuableFeatures: valuableFeaturesSchema,
      returnLikelihood: z.nativeEnum(ReturnLikelihood),
    }),
    subscriptionId: z.string().min(1, "Subscription ID is required"),
    planId: z.string().optional(),
    addonPlanId: z.string().optional(),
    quantity: z.number().min(1, "Quantity must be at least 1"),
    assignedMailboxIds: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (!data.planId && !data.addonPlanId) {
        return false;
      }
      return true;
    },
    {
      message: "Plan ID and Addon Plan ID are required",
      path: ["planId", "addonPlanId"],
    }
  );

const CancelPlanContext = createContext({
  state: {
    step: 1,
    selectedAssignedMailboxes: [] as string[],
    feedback: undefined as FeedbackFormValues | undefined,
    showConfirmationDialog: false as boolean,
    isSuccess: false as boolean,
  },
  handleStateChange: (newPartialState: Partial<CancelPlanState>) => {},
  subscription: {} as IActiveSubscriptionPlan | undefined,
  planDetails: {} as IPurchasePlan | undefined,
  searchParams: {} as CancelPlanSearch,
  handleConfirm: () => {},
  isCancellationPending: false as boolean,
});

type CancelPlanState = {
  step: number;
  selectedAssignedMailboxes: string[];
  feedback: FeedbackFormValues | undefined;
  showConfirmationDialog: boolean;
  isSuccess: boolean;
};

export const CancelPlanProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const search = useSearch({ from: "/cancelPlan" });

  const [state, setState] = useState<CancelPlanState>({
    step: 1,
    selectedAssignedMailboxes: [] as string[],
    feedback: undefined,
    showConfirmationDialog: false,
    isSuccess: false,
  });

  const handleStateChange = (newPartialState: Partial<CancelPlanState>) => {
    setState((prevState) => ({ ...prevState, ...newPartialState }));
  };

  // Get subscription data from API
  const { data: activePlans } = useGetActiveSubscriptionPlans();

  const subscription = useMemo(() => {
    return activePlans?.find((sub) => sub._id === search.subscriptionId);
  }, [activePlans, search.subscriptionId]);

  const planDetails = useMemo(() => {
    return subscription?.plans.find(
      (plan) => plan.planId?._id === search.planId
    );
  }, [subscription, search.planId]);

  const {
    mutateAsync: sendCancellationRequest,
    isPending: isCancellationPending,
  } = useSendPlanCancellationRequest();

  const handleConfirm = async () => {
    try {
      if (!planDetails || !subscription) {
        toast.error("Plan details or subscription not found");
        return;
      }

      if (!state.feedback) {
        toast.error("Please provide feedback before cancelling");
        return;
      }

      if (
        planDetails.quantity !== search.quantity &&
        planDetails.assignedMailboxes.length !== 0 &&
        state.selectedAssignedMailboxes.length === 0
      ) {
        toast.error("You must select at least one mailbox to cancel");
        return;
      }

      const payload: IPlanCancellationRequestPayload = {
        feedback: {
          cancellationReason: formatCancellationReason(state.feedback),
          improvementSuggestions: state.feedback?.improvementFeedback,
          serviceRating: state.feedback?.rating || 0,
          valuableFeatures: state.feedback?.valuableFeatures,
          returnLikelihood: state.feedback?.returnLikelihood,
        },
        subscriptionId: subscription._id,
        planId: planDetails.planId?._id,
        addonPlanId: planDetails.addonPlanId?._id,
        quantity: search.quantity,
        assignedMailboxIds: state.selectedAssignedMailboxes,
      };
      console.log(payload);

      // Validate payload before sending
      const validatedPayload = cancellationPayloadSchema.parse(payload);

      // Use validated payload for the API call
      await sendCancellationRequest(validatedPayload);

      window.cioanalytics?.track("plan_cancelled", {
        plan: planDetails.planId?.planType,
        subscriptionId: subscription._id,
        quantity: search.quantity,
        assignedMailboxCount: state.selectedAssignedMailboxes.length,
      });

      handleStateChange({
        showConfirmationDialog: false,
        isSuccess: true,
      });
      toast.success("Subscription cancellation confirmed");
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Format Zod error messages
        const errorMessages = error.errors.map((err) => err.message).join("\n");
        toast.error(`Validation Error: ${errorMessages}`);
      } else {
        toast.error("Error cancelling subscription");
        console.error(error);
      }
    }
  };

  return (
    <CancelPlanContext.Provider
      value={{
        state,
        handleStateChange,
        subscription,
        planDetails,
        searchParams: search,
        handleConfirm,
        isCancellationPending,
      }}
    >
      {children}
    </CancelPlanContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCancelPlanContext = () => {
  const context = useContext(CancelPlanContext);
  if (!context) {
    throw new Error("useCancelPlan must be used within a CancelPlanProvider");
  }
  return context;
};
